import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { AiError, askAssistant, fetchAiHistory } from "./api";
import { EMPTY_ANSWER } from "./normalize";
import type { AiAnswer, AiFilters, AiMessage } from "./types";

/** A search the user kept from an empty answer («Издөөнү сактоо»). */
export interface SavedAiSearch {
  id: string;
  query: string;
  filters: AiFilters | null;
  createdAt: string;
}

interface AiChatState {
  /** Server conversation (POST /api/ai/search answers with it). */
  conversationId: string | null;
  messages: AiMessage[];
  saved: SavedAiSearch[];
  /** Text handed over from the home page / FAB, sent once the chat opens. */
  pendingPrompt: string | null;
  busy: boolean;
  /** Last error code — 429 shows its own line. */
  error: string | null;
  historyLoaded: boolean;
  send: (text: string) => Promise<void>;
  retry: (messageId: string) => void;
  stop: () => void;
  queue: (text: string) => void;
  takePending: () => string | null;
  loadHistory: () => Promise<void>;
  saveSearch: (query: string, filters: AiFilters | null) => boolean;
  removeSaved: (id: string) => void;
  newChat: () => void;
  clear: () => void;
}

const MAX_MESSAGES = 60;
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

let controller: AbortController | null = null;

export const useAiChat = create<AiChatState>()(
  persist(
    (set, get) => {
      const patchAnswer = (id: string, patch: Partial<Extract<AiMessage, { role: "assistant" }>>) =>
        set((s) => ({
          messages: s.messages.map((m) => (m.id === id && m.role === "assistant" ? { ...m, ...patch } : m)),
        }));

      const run = async (text: string, assistantId: string) => {
        controller?.abort();
        const ctrl = new AbortController();
        controller = ctrl;
        set({ busy: true, error: null });
        try {
          const { answer, conversationId } = await askAssistant({
            message: text,
            conversationId: get().conversationId,
            signal: ctrl.signal,
            onUpdate: (a: AiAnswer, stage) =>
              patchAnswer(assistantId, { answer: a, status: stage === "searching" && !a.text ? "pending" : "streaming" }),
          });
          patchAnswer(assistantId, { answer, status: "done" });
          set({ conversationId });
        } catch (e) {
          if (ctrl.signal.aborted) {
            // Stopped by the user — keep what arrived.
            const m = get().messages.find((x) => x.id === assistantId);
            if (m && m.role === "assistant" && m.answer.text) patchAnswer(assistantId, { status: "done" });
            else set((s) => ({ messages: s.messages.filter((x) => x.id !== assistantId) }));
          } else {
            const code = e instanceof AiError ? e.code : "AI_NETWORK";
            // A conversation that's not ours any more (other account / expired guest) — start a new one.
            if (code === "AI_FORBIDDEN") set({ conversationId: null });
            patchAnswer(assistantId, { status: "error" });
            set({ error: code });
          }
        } finally {
          if (controller === ctrl) controller = null;
          set({ busy: false });
        }
      };

      return {
        conversationId: null,
        messages: [],
        saved: [],
        pendingPrompt: null,
        busy: false,
        error: null,
        historyLoaded: false,

        send: async (raw) => {
          const text = raw.trim().slice(0, 500);
          if (!text || get().busy) return;
          const now = new Date().toISOString();
          const assistantId = uid();
          set((s) => ({
            messages: [
              ...s.messages,
              { id: uid(), role: "user" as const, text, createdAt: now },
              { id: assistantId, role: "assistant" as const, createdAt: now, status: "pending" as const, answer: EMPTY_ANSWER, replyTo: text },
            ].slice(-MAX_MESSAGES),
          }));
          await run(text, assistantId);
        },

        retry: (messageId) => {
          const m = get().messages.find((x) => x.id === messageId);
          if (!m || m.role !== "assistant" || get().busy) return;
          patchAnswer(messageId, { status: "pending", answer: EMPTY_ANSWER, createdAt: new Date().toISOString() });
          void run(m.replyTo, messageId);
        },

        stop: () => controller?.abort(),

        queue: (text) => set({ pendingPrompt: text.trim() || null }),
        takePending: () => {
          const p = get().pendingPrompt;
          if (p) set({ pendingPrompt: null });
          return p;
        },

        loadHistory: async () => {
          const { conversationId, messages, historyLoaded } = get();
          if (historyLoaded || !conversationId) {
            set({ historyLoaded: true });
            return;
          }
          set({ historyLoaded: true });
          // Only fill an empty device (another browser, cleared storage) — local turns are newer.
          if (messages.length > 0) return;
          try {
            const items = await fetchAiHistory(conversationId);
            if (get().messages.length === 0) set({ messages: items.slice(-MAX_MESSAGES) });
          } catch {
            /* history is a nicety */
          }
        },

        saveSearch: (query, filters) => {
          const key = JSON.stringify(filters ?? query);
          if (get().saved.some((s) => JSON.stringify(s.filters ?? s.query) === key)) return false;
          set((s) => ({ saved: [{ id: uid(), query, filters, createdAt: new Date().toISOString() }, ...s.saved].slice(0, 20) }));
          return true;
        },
        removeSaved: (id) => set((s) => ({ saved: s.saved.filter((x) => x.id !== id) })),

        newChat: () => {
          controller?.abort();
          set({ conversationId: null, messages: [], error: null, busy: false });
        },
        clear: () => {
          controller?.abort();
          set({ conversationId: null, messages: [], saved: [], pendingPrompt: null, error: null, busy: false, historyLoaded: false });
        },
      };
    },
    {
      name: "turaq-ai-chat",
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        conversationId: s.conversationId,
        saved: s.saved,
        // An answer cut off by a reload is shown as failed, with «Кайра аракет кылыңыз».
        messages: s.messages.map((m) => (m.role === "assistant" && m.status !== "done" ? { ...m, status: "error" as const } : m)),
      }),
    },
  ),
);
